import "@/App.css";
import { useState } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { WorkspaceProvider, useWorkspace } from "@/context/WorkspaceContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { ConfirmProvider } from "@/components/ConfirmDialog";
import { ScrollToTop, ScrollTopButton } from "@/components/ScrollHelpers";
import { MarketingShell } from "@/components/MarketingShell";
import { AppShell } from "@/components/AppShell";
import AuthPage from "@/pages/AuthPage";
import MarketingHomePage from "@/pages/MarketingHomePage";
import { PrivacyPolicyPage, TermsOfServicePage, DPAPage } from "@/pages/LegalPages";
import ContactPage from "@/pages/ContactPage";
import RecorderPage from "@/pages/RecorderPage";
import TestDetailPage from "@/pages/TestDetailPage";
import RunResultPage from "@/pages/RunResultPage";
import AuthStatesPage from "@/pages/AuthStatesPage";
import WorkspaceSettingsPage from "@/pages/WorkspaceSettingsPage";
import TeamMembersPage from "@/pages/TeamMembersPage";
import ProfilePage from "@/pages/ProfilePage";
import AcceptInvitePage from "@/pages/AcceptInvitePage";
import JoinWorkspacePage from "@/pages/JoinWorkspacePage";
import AdminCustomersPage from "@/pages/admin/AdminCustomersPage";
import AdminWorkspacesPage from "@/pages/admin/AdminWorkspacesPage";
import AdminPlatformSettingsPage from "@/pages/admin/AdminPlatformSettingsPage";
import AdminsPage from "@/pages/admin/AdminsPage";
import ProjectsPage from "@/pages/projects/ProjectsPage";
import ProjectLayout from "@/pages/projects/ProjectLayout";
import ProjectOverviewPage from "@/pages/projects/ProjectOverviewPage";
import ProjectSuitesPage from "@/pages/projects/ProjectSuitesPage";
import ProjectTestsPage from "@/pages/projects/ProjectTestsPage";
import ProjectRunsPage from "@/pages/projects/ProjectRunsPage";
import ProjectSchedulesPage from "@/pages/projects/ProjectSchedulesPage";
import SuiteDetailPage from "@/pages/projects/SuiteDetailPage";
import SuiteRunPage from "@/pages/projects/SuiteRunPage";
import ProjectEnvironmentsPage from "@/pages/projects/ProjectEnvironmentsPage";
import EnvironmentDetailPage from "@/pages/projects/EnvironmentDetailPage";
import WorkspaceTestsPage from "@/pages/WorkspaceTestsPage";
import WorkspaceBinPage from "@/pages/WorkspaceBinPage";
import SchedulesPage from "@/pages/SchedulesPage";

function ProtectedLayout() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-sm text-[#64748B]" data-testid="auth-loading">
          Authenticating…
        </div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function ProtectedLayoutNoShell() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-sm text-[#64748B]">Authenticating…</div>
      </div>
    );
  }
  if (user === false) return <Navigate to="/login" replace />;
  return <Outlet />;
}

function RequireSuperAdmin() {
  const { user } = useAuth();
  if (user === null) return null;
  if (user === false) return <Navigate to="/login" replace />;
  if (user?.global_role !== "super_admin") return <Navigate to="/" replace />;
  return <Outlet />;
}

function RequireWorkspace() {
  const { workspaces, current, loading, refresh, switchTo } = useWorkspace();
  const { user } = useAuth();
  if (loading || workspaces === null) {
    return (
      <div className="px-6 py-10 text-sm text-[#64748B]" data-testid="workspace-loading">
        Loading workspace…
      </div>
    );
  }
  if (workspaces.length === 0) {
    return <NoWorkspaceEmptyState user={user} refresh={refresh} switchTo={switchTo} />;
  }
  // Keying the Outlet by the active workspace id forces every workspace-
  // scoped page (Projects, Test Cases, Schedules, Team Members, Bin, etc.)
  // to remount + refetch when the user switches workspace. Without this,
  // pages that already loaded data for the previous workspace would keep
  // showing stale rows because their `useEffect` deps don't include the
  // workspace id (and the api layer pulls X-Workspace-Id from localStorage
  // at request time, so the only way to refresh is to re-fire the effect).
  return <Outlet key={current?.id || "no-workspace"} />;
}

function NoWorkspaceEmptyState({ user, refresh, switchTo }) {
  const isSuperAdmin = user?.global_role === "super_admin";
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);

  const handleCreate = async (e) => {
    e?.preventDefault();
    if (!name.trim() || creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/workspaces", { name: name.trim() });
      localStorage.setItem("probe_workspace_id", data.id);
      switchTo(data.id);
      await refresh();
      toast.success(`Workspace "${data.name}" is ready.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-16" data-testid="no-workspace">
      <div className="text-center">
        <p className="text-xs font-medium text-[#3B82F6]">No workspace</p>
        <h2 className="font-['Geist'] font-semibold text-2xl mt-2 text-[#0F172A]">
          You aren&apos;t a member of any workspace yet.
        </h2>
        <p className="text-sm text-[#64748B] mt-3">
          {isSuperAdmin
            ? "Spin one up to start running tests, or invite teammates afterwards."
            : "Ask a teammate for an invite, or create your own workspace below."}
        </p>
      </div>
      <form
        onSubmit={handleCreate}
        className="mt-8 border border-[#E5E7EB] rounded-2xl p-5 bg-white"
        data-testid="no-workspace-create-form"
      >
        <label className="text-sm font-medium text-[#475569] block mb-1.5" htmlFor="ws-name">
          Workspace name
        </label>
        <Input
          id="ws-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={isSuperAdmin ? "Probe HQ" : "Acme QA"}
          required
          autoFocus
          data-testid="no-workspace-name-input"
          className="focus-visible:ring-[#3B82F6]"
        />
        <button
          type="submit"
          disabled={creating || !name.trim()}
          data-testid="no-workspace-create-submit"
          className="mt-4 h-10 w-full inline-flex items-center justify-center gap-2 text-sm font-semibold rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60"
        >
          {creating ? "Creating…" : "Create workspace"}
        </button>
      </form>
    </div>
  );
}

function PublicOrDashboard() {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <span className="text-sm text-[#64748B]" data-testid="auth-loading">Loading…</span>
      </div>
    );
  }
  if (user === false) return <MarketingHomePage />;
  // Authed users land on the new Projects hub
  return <Navigate to={user?.global_role === "super_admin" ? "/admin/customers" : "/projects"} replace />;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <WorkspaceProvider>
          <ConfirmProvider>
            <BrowserRouter>
              <ScrollToTop />
              <ScrollTopButton />
              <Routes>
                <Route path="/" element={<PublicOrDashboard />} />
                <Route path="/legal/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/legal/terms" element={<TermsOfServicePage />} />
                <Route path="/legal/dpa" element={<DPAPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route
                  path="/login"
                  element={<MarketingShell><AuthPage key="login" defaultMode="login" /></MarketingShell>}
                />
                <Route
                  path="/signup"
                  element={<MarketingShell><AuthPage key="signup" defaultMode="register" /></MarketingShell>}
                />
                {/* Public — the page itself decides whether to sign in / sign up / accept */}
                <Route path="/accept-invite" element={<MarketingShell><AcceptInvitePage /></MarketingShell>} />
                <Route path="/join/:token" element={<JoinWorkspacePage />} />
                <Route element={<ProtectedLayout />}>
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route element={<RequireSuperAdmin />}>
                    <Route path="/admin" element={<Navigate to="/admin/customers" replace />} />
                    <Route path="/admin/customers" element={<AdminCustomersPage />} />
                    <Route path="/admin/workspaces" element={<AdminWorkspacesPage />} />
                    <Route path="/admin/admins" element={<AdminsPage />} />
                    <Route path="/admin/platform-settings" element={<AdminPlatformSettingsPage />} />
                  </Route>
                  <Route element={<RequireWorkspace />}>
                    <Route path="/projects" element={<ProjectsPage />} />
                    <Route path="/projects/:projectId" element={<ProjectLayout />}>
                      <Route index element={<ProjectOverviewPage />} />
                      <Route path="environments" element={<ProjectEnvironmentsPage />} />
                      <Route path="suites" element={<ProjectSuitesPage />} />
                      <Route path="tests" element={<ProjectTestsPage />} />
                      <Route path="runs" element={<ProjectRunsPage />} />
                      <Route path="schedules" element={<ProjectSchedulesPage />} />
                    </Route>
                    <Route path="/projects/:projectId/environments/:envId" element={<EnvironmentDetailPage />} />
                    <Route path="/projects/:projectId/suites/:suiteId" element={<SuiteDetailPage />} />
                    <Route path="/tests" element={<WorkspaceTestsPage />} />
                    <Route path="/schedules" element={<SchedulesPage />} />
                    <Route path="/bin" element={<WorkspaceBinPage />} />
                    <Route path="/auth-states" element={<AuthStatesPage />} />
                    <Route path="/workspace/settings" element={<WorkspaceSettingsPage />} />
                    <Route path="/workspace/members" element={<TeamMembersPage />} />
                  </Route>
                </Route>
                {/* Test-work routes run full-screen without the app shell */}
                <Route element={<ProtectedLayoutNoShell />}>
                  <Route element={<RequireWorkspace />}>
                    <Route path="/record" element={<RecorderPage />} />
                    <Route path="/tests/:id" element={<TestDetailPage />} />
                    <Route path="/runs/:id" element={<RunResultPage />} />
                    <Route path="/suite-runs/:runId" element={<SuiteRunPage />} />
                  </Route>
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
            <Toaster position="bottom-right" />
          </ConfirmProvider>
        </WorkspaceProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
